import { createIsomorphicFn } from '@tanstack/react-start'
import { useParams } from '@tanstack/react-router'
import i18n from '@/lib/i18n'

export const SUPPORTED_LANGS = ['en', 'es', 'pt', 'zh', 'hi', 'tl', 'fr'] as const
export type Lang = (typeof SUPPORTED_LANGS)[number]

export function useLang(): Lang {
  const params = useParams({ strict: false }) as { lang?: string }
  if (params.lang && SUPPORTED_LANGS.includes(params.lang as Lang)) {
    return params.lang as Lang
  }
  const detected = i18n.resolvedLanguage ?? 'en'
  return (SUPPORTED_LANGS.includes(detected as Lang) ? detected : 'en') as Lang
}

// Returns the user's preferred language for redirect routes.
// Server: parses the Accept-Language request header (dynamic import keeps the
//   server module out of the client bundle — the compiler strips .server() entirely).
// Client: reads i18n.resolvedLanguage, already detected from URL → localStorage → navigator.
export const getPreferredLang = createIsomorphicFn()
  .server(async () => {
    const { getRequestHeader } = await import('@tanstack/react-start/server')
    const header = getRequestHeader('accept-language') ?? ''
    for (const part of header.split(',')) {
      const primary = part.split(';')[0].trim().split('-')[0].toLowerCase()
      const lang = SUPPORTED_LANGS.find(l => l === primary)
      if (lang) return lang
    }
    return 'en' as Lang
  })
  .client(async () => {
    const detected = i18n.resolvedLanguage ?? 'en'
    return (SUPPORTED_LANGS.includes(detected as Lang) ? detected : 'en') as Lang
  })
